import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';

const getFieldGroups = (role) => {
  if (role === 'builder') {
    return [
      {
        title: 'Builder Information',
        fields: [
          { key: 'company_name', label: 'Business Name' },
          { key: 'full_name', label: 'Contact Person' },
          { key: 'email', label: 'Email', type: 'email' },
          { key: 'phone', label: 'Phone', type: 'tel' },
          { key: 'rep_phone', label: 'Representative Phone', type: 'tel' },
          { key: 'website_url', label: 'Website', type: 'url' },
        ],
      },
      {
        title: 'Location',
        fields: [
          { key: 'address', label: 'Address', multiline: true },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'pincode', label: 'Pincode', type: 'number' },
        ],
      },
      {
        title: 'About',
        fields: [
          { key: 'bio', label: 'Bio', multiline: true },
          { key: 'experience', label: 'Experience' },
        ],
      },
    ];
  }
  return [
    {
      title: 'Personal Information',
      fields: [
        { key: 'full_name', label: 'Full Name' },
        { key: 'first_name', label: 'First Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'phone', label: 'Phone', type: 'tel' },
        ...(role === 'agency' ? [
          { key: 'company_name', label: 'Company Name' },
          { key: 'rep_phone', label: 'Representative Phone', type: 'tel' },
        ] : []),
      ],
    },
    {
      title: 'Location',
      fields: [
        { key: 'address', label: 'Address', multiline: true },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'pincode', label: 'Pincode', type: 'number' },
      ],
    },
    {
      title: 'About',
      fields: [
        { key: 'bio', label: 'Bio', multiline: true },
        { key: 'experience', label: 'Experience' },
      ],
    },
  ];
};

export default function AdminCrmPersonalInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole, userEmail, userPhone } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const [editedFields, setEditedFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const fieldGroups = getFieldGroups(userRole);

  useEffect(() => { if (userId) fetchProfile(); }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await adminCrmService.getFullProfile(userId);
      if (res.success && res.data) {
        setProfile(res.data);
        const initial = {};
        fieldGroups.forEach(g => g.fields.forEach(f => { initial[f.key] = res.data[f.key] || ''; }));
        setEditedFields(initial);
      }
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const handleFieldChange = (key, value) => {
    setEditedFields(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const changed = {};
      fieldGroups.forEach(g => g.fields.forEach(f => {
        if ((profile[f.key] || '') !== (editedFields[f.key] || '')) changed[f.key] = editedFields[f.key];
      }));
      if (Object.keys(changed).length === 0) { toast.info('No fields were modified'); setSaving(false); return; }
      const res = await adminCrmService.updateProfile(userId, changed);
      if (res.success) {
        toast.success('Profile updated successfully');
        setHasChanges(false);
        if (res.data) setProfile(res.data);
      } else {
        toast.error(res.message || 'Failed to update');
      }
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const headerLabel = userRole === 'builder' ? 'Builder Information' : 'Personal Information';

  return (
    <div className="d-flex" style={{ background: '#fff', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-3">

          {/* Back + Title + Save */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <h6 className="accordion-title mb-0">{headerLabel}</h6>
            </div>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                background: 'none', border: 'none', cursor: hasChanges ? 'pointer' : 'default',
                fontSize: '15px', fontWeight: '600', color: '#2563EB',
                opacity: hasChanges ? 1 : 0.4
              }}
            >{saving ? 'Saving...' : 'Save'}</button>
          </div>

          {/* {userName && <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>{userName}</p>} */}

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><div className="mt-3" style={{ color: '#888' }}>Loading...</div></div>
          ) : (
            <>
              {fieldGroups.map(group => (
                <div key={group.title} className="mb-3">
                  <h6 className="accordion-title mb-2" style={{ fontSize: '14px', color: '#6B7280' }}>{group.title}</h6>
                  <div className="card p-4 personal-ids-card">
                    {group.fields.map(field => (
                      <div key={field.key} className="form-group mb-3">
                        <label className="form-label personal-label">{field.label}</label>
                        {field.multiline ? (
                          <textarea
                            className="form-control personal-input"
                            rows="3"
                            value={editedFields[field.key] || ''}
                            onChange={e => handleFieldChange(field.key, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        ) : (
                          <input
                            type={field.type || 'text'}
                            className="form-control personal-input"
                            value={editedFields[field.key] || ''}
                            onChange={e => handleFieldChange(field.key, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Account Info (read-only) */}
              <div className="mb-3">
                <h6 className="accordion-title mb-2" style={{ fontSize: '14px', color: '#6B7280' }}>Account Info</h6>
                <div className="card p-4 personal-ids-card">
                  <div className="d-flex justify-content-between py-2" style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <span className="personal-label">User ID</span>
                    <span style={{ fontSize: '13px', color: '#111827', fontWeight: '500', userSelect: 'text' }}>{profile.id}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="personal-label">Registered</span>
                    <span style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                      {profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
